import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import type { BookingScheduleInterface } from "../../../features/Booking-Form/typings/booking.types";
import type { ServiceOrderDto } from "../../FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/Domain/BookingServicePrompt.types";
import { useRemoteChangeConferenceRoom } from "../../../hooks/Remote/Schedule/useRemoteChangeConferenceRoom";
import type {
  ChangeConferenceRoomSelection,
  ChangeConferenceRoomStep
} from "./change-conference-room.types";
import { ChangeConferenceRoomReadOnlySummary } from "./change-conference-room-read-only-summary.partial";
import { ChangeConferenceRoomFloorStep } from "./change-conference-room-floor-step.partial";
import { ChangeConferenceRoomServicesStep } from "./change-conference-room-services-step.partial";
import type { IFloorPayload } from "../../FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import { useRemoteMutateFetchFloorPlanService } from "../../../hooks/Remote/FloorPlan/useRemoteMutateFetchFloorPlanService";
import {
  getChangeRoomPlanDateRange,
  isChangeRoomSubjectEditable,
  isChangeRoomSubjectValid,
  newRoomRequiresServiceReselection,
  normalizeChangeRoomSubject,
  serviceOrderToUpdateServicesDto
} from "./change-conference-room.functions";

type P = {
  open: boolean;
  onClose: () => void;
  schedule: BookingScheduleInterface;
  activateBookingServices: boolean;
  refetchAllSchedule?: () => void;
};

export function ChangeConferenceRoomDialog({
  open,
  onClose,
  schedule,
  activateBookingServices,
  refetchAllSchedule
}: Readonly<P>) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [step, setStep] = useState<ChangeConferenceRoomStep>("pickRoom");
  const [subject, setSubject] = useState(() => normalizeChangeRoomSubject(schedule.subject ?? ""));
  const [selection, setSelection] = useState<ChangeConferenceRoomSelection | null>(null);
  const [floorPlanForServices, setFloorPlanForServices] = useState<IFloorPayload | undefined>();
  const [serviceOrder, setServiceOrder] = useState<ServiceOrderDto | null>(null);
  const [servicesStepValid, setServicesStepValid] = useState(false);
  const [servicesStepEntered, setServicesStepEntered] = useState(false);
  const [servicesStepVisitId, setServicesStepVisitId] = useState(0);
  const [isFloorPlanForServicesLoading, setIsFloorPlanForServicesLoading] = useState(false);
  const [servicesStepUIState, setServicesStepUIState] = useState({
    loading: false,
    hasConfigurableTabs: false,
    canProceed: false
  });

  const { mutateAsync: changeRoom, isLoading: isSubmitting } = useRemoteChangeConferenceRoom();
  const { mutateAsync: fetchFloorPlanForService } = useRemoteMutateFetchFloorPlanService();

  const currentRoomLabel = schedule.bookingInventory.name ?? "";
  const isSubjectEditable = useMemo(() => isChangeRoomSubjectEditable(schedule), [schedule]);
  const isSubjectValid = isChangeRoomSubjectValid(subject);

  useEffect(() => {
    if (open) {
      setSubject(normalizeChangeRoomSubject(schedule.subject ?? ""));
    }
  }, [open, schedule.subject]);

  const handleSubjectEdit = useCallback(
    async (value: string) => {
      const trimmed = normalizeChangeRoomSubject(value);
      if (!trimmed) {
        enqueueSnackbar(t("Subject is required"), { variant: "error" });
        throw new Error("Subject is required");
      }
      setSubject(trimmed);
    },
    [enqueueSnackbar, t]
  );

  const showServicesStep = useMemo(() => {
    if (!selection) {
      return false;
    }
    return newRoomRequiresServiceReselection(
      floorPlanForServices,
      selection.newZoneScheduleId,
      activateBookingServices
    );
  }, [activateBookingServices, floorPlanForServices, selection]);

  useEffect(() => {
    if (!selection || !activateBookingServices) {
      setFloorPlanForServices(undefined);
      setIsFloorPlanForServicesLoading(false);
      return;
    }

    const planRange = getChangeRoomPlanDateRange(schedule);
    if (!planRange) {
      setFloorPlanForServices(undefined);
      setIsFloorPlanForServicesLoading(false);
      return;
    }

    let cancelled = false;
    setFloorPlanForServices(undefined);
    setIsFloorPlanForServicesLoading(true);

    fetchFloorPlanForService({
      floorInventoryId: selection.newFloorInventoryId,
      dto: { end: planRange.end, start: planRange.start }
    })
      .then(data => {
        if (!cancelled) {
          setFloorPlanForServices(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFloorPlanForServices(undefined);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsFloorPlanForServicesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activateBookingServices, fetchFloorPlanForService, schedule, selection?.newFloorInventoryId]);

  const handlePickRoomChange = useCallback(
    (nextSelection: ChangeConferenceRoomSelection | null) => {
      setSelection(prev => {
        const selectionUnchanged =
          prev?.newBookingInventoryId === nextSelection?.newBookingInventoryId &&
          prev?.newZoneScheduleId === nextSelection?.newZoneScheduleId &&
          (prev != null) === (nextSelection != null);

        if (selectionUnchanged) {
          return prev;
        }
        return nextSelection;
      });
    },
    []
  );

  const selectionKey = selection
    ? `${selection.newBookingInventoryId}-${selection.newZoneScheduleId}`
    : null;
  const previousSelectionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (previousSelectionKeyRef.current === selectionKey) {
      return;
    }
    if (previousSelectionKeyRef.current != null) {
      setServiceOrder(null);
      setServicesStepValid(false);
      setServicesStepEntered(false);
      setServicesStepVisitId(0);
      if (selectionKey == null) {
        setFloorPlanForServices(undefined);
      }
    }
    previousSelectionKeyRef.current = selectionKey;
  }, [selectionKey]);

  const resetState = useCallback(() => {
    setStep("pickRoom");
    setSubject(normalizeChangeRoomSubject(schedule.subject ?? ""));
    setSelection(null);
    setFloorPlanForServices(undefined);
    setServiceOrder(null);
    setServicesStepValid(false);
    setServicesStepEntered(false);
    setServicesStepVisitId(0);
    setIsFloorPlanForServicesLoading(false);
    previousSelectionKeyRef.current = null;
  }, [schedule.subject]);

  const shouldEnterServicesStep = useMemo(() => {
    if (!selection || !activateBookingServices) {
      return false;
    }
    if (isFloorPlanForServicesLoading) {
      return true;
    }
    return showServicesStep;
  }, [activateBookingServices, isFloorPlanForServicesLoading, selection, showServicesStep]);

  const shouldMountServicesStep =
    selection != null &&
    servicesStepEntered &&
    (step === "services" || step === "confirm") &&
    (shouldEnterServicesStep ||
      servicesStepUIState.loading ||
      servicesStepUIState.hasConfigurableTabs);

  useEffect(() => {
    if (step === "services" && !servicesStepEntered) {
      setStep("pickRoom");
      setServicesStepValid(false);
      setServiceOrder(null);
    }
  }, [servicesStepEntered, step]);

  useEffect(() => {
    if (step === "pickRoom") {
      setServicesStepUIState({ loading: false, hasConfigurableTabs: false, canProceed: false });
    }
  }, [step]);

  useEffect(() => {
    if (step !== "services" || servicesStepUIState.loading) {
      return;
    }
    if (!servicesStepUIState.hasConfigurableTabs) {
      setServicesStepValid(true);
      setStep("confirm");
    }
  }, [servicesStepUIState, step]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleBack = () => {
    if (step === "confirm") {
      if (servicesStepEntered && servicesStepUIState.hasConfigurableTabs) {
        setStep("services");
        return;
      }
      setServicesStepValid(false);
      setServiceOrder(null);
      setServicesStepEntered(false);
      setStep("pickRoom");
      return;
    }
    if (step === "services") {
      setServicesStepValid(false);
      setServiceOrder(null);
      setServicesStepEntered(false);
      setStep("pickRoom");
    }
  };

  const handleNextFromPickRoom = () => {
    if (!selection) {
      return;
    }
    if (shouldEnterServicesStep) {
      setServicesStepValid(false);
      setServiceOrder(null);
      setServicesStepEntered(true);
      setServicesStepVisitId(visitId => visitId + 1);
      setServicesStepUIState({ loading: true, hasConfigurableTabs: false, canProceed: false });
      setStep("services");
      return;
    }
    setStep("confirm");
  };

  useEffect(() => {
    if (
      step !== "services" ||
      isFloorPlanForServicesLoading ||
      servicesStepUIState.loading ||
      !servicesStepEntered
    ) {
      return;
    }
    if (!showServicesStep) {
      setServicesStepValid(true);
      setStep("confirm");
    }
  }, [
    isFloorPlanForServicesLoading,
    servicesStepEntered,
    servicesStepUIState.loading,
    showServicesStep,
    step
  ]);

  const handleNextFromServices = () => {
    if (!servicesStepValid) {
      return;
    }
    setStep("confirm");
  };

  const handleServicesStepStateChange = useCallback(
    (state: { loading: boolean; hasConfigurableTabs: boolean; canProceed: boolean }) => {
      setServicesStepUIState(state);
      setServicesStepValid(state.canProceed);
    },
    []
  );

  const handleConfirm = async () => {
    if (!selection || !isSubjectValid) {
      return;
    }
    const bookingServicesDto =
      showServicesStep && serviceOrder
        ? serviceOrderToUpdateServicesDto(schedule, serviceOrder)
        : undefined;

    try {
      await changeRoom({
        bookingId: schedule.id,
        newBookingInventoryId: selection.newBookingInventoryId,
        subject: normalizeChangeRoomSubject(subject),
        bookingServices: bookingServicesDto ? [bookingServicesDto] : undefined
      });
      refetchAllSchedule?.();
      handleClose();
    } catch {
      // Error snackbar handled by useRemoteMutation
    }
  };

  const dialogTitle = t("_changeRoom_dialogTitle");

  const canGoNext =
    step === "pickRoom" ? selection != null : step === "services" ? servicesStepValid : false;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      data-testid="change-conference-room-dialog"
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <ChangeConferenceRoomReadOnlySummary
          schedule={schedule}
          subject={subject}
          isSubjectEditable={isSubjectEditable}
          onSubjectEdit={handleSubjectEdit}
          currentRoomLabel={currentRoomLabel}
          newRoomLabel={selection?.newRoomLabel}
        />

        <Box sx={{ display: step === "pickRoom" ? "block" : "none" }}>
          <ChangeConferenceRoomFloorStep
            schedule={schedule}
            currentSelection={selection}
            onSelectionChange={handlePickRoomChange}
          />
        </Box>

        {shouldMountServicesStep && selection && (
          <Box sx={{ display: step === "services" ? "block" : "none" }}>
            <ChangeConferenceRoomServicesStep
              key={`${selection.newBookingInventoryId}-${selection.newZoneScheduleId}-${servicesStepVisitId}`}
              schedule={schedule}
              selection={selection}
              onOrderChange={setServiceOrder}
              onStepStateChange={handleServicesStepStateChange}
            />
          </Box>
        )}

        {step === "confirm" && selection && (
          <Typography variant="body1" data-testid="change-room-confirm-body">
            {t("_changeRoom_confirmBody", {
              fromRoom: currentRoomLabel,
              toRoom: selection.newRoomLabel
            })}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 2, py: 1.5 }}>
        <Button onClick={handleClose} color="secondary" data-testid="change-room-cancel">
          {t("Cancel")}
        </Button>
        {step !== "pickRoom" && (
          <Button onClick={handleBack} data-testid="change-room-back" color="secondary">
            {t("Back")}
          </Button>
        )}
        <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
          {step === "pickRoom" && (
            <Button
              disabled={!canGoNext}
              onClick={handleNextFromPickRoom}
              data-testid="change-room-next"
            >
              {t("Next")}
            </Button>
          )}
          {step === "services" &&
            !servicesStepUIState.loading &&
            servicesStepUIState.hasConfigurableTabs && (
              <Button
                disabled={!canGoNext}
                onClick={handleNextFromServices}
                data-testid="change-room-next-services"
              >
                {t("_changeRoom_review")}
              </Button>
            )}
          {step === "confirm" && (
            <Button
              disabled={isSubmitting || !isSubjectValid}
              onClick={handleConfirm}
              data-testid="change-room-confirm"
            >
              {t("Confirm")}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
